import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import MenuCart from '@/components/MenuCart';
import axios from 'axios';
import { 
  ChevronDown,
  ChevronUp,
  Flame,
  X,
  Gift,
  Sparkles,
  Star,
  ChevronRight,
  ChevronLeft,
  Clock,
  MapPin,
  AlertTriangle,
  Phone,
  Facebook,
  Instagram,
  Globe
} from 'lucide-react';

import { UtensilsCrossed, ShoppingCart, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// UI translations
const UI_TEXTS = {
  fr: {
    popular: "Populaires",
    dineIn: "Sur place",
    takeaway: "À emporter",
    closedToday: "Fermé aujourd'hui",
    open: "Ouvert",
    closed: "Fermé",
    openingHours: "Horaires d'ouverture",
    today: "Aujourd'hui",
    viewMenu: "Voir le menu",
    allergens: "Allergènes",
    youMightLike: "Vous aimerez aussi",
    orderMessage: "Pour commander, veuillez vous adresser à notre équipe",
    menuPrep: "Menu en préparation",
    menuPrepDesc: "Ce restaurant prépare son menu. Revenez bientôt !",
    poweredBy: "Propulsé par",
    specialOffer: "Offre spéciale",
    viewOffer: "Voir l'offre spéciale →",
    yourReviewMatters: "Votre avis compte !",
    leaveReview: "Laisser un avis",
    unavailable: "Indisponible",
    hours: "Horaires",
    top: "Top",
    translating: "Traduction...",
    dailySpecial: "Menu du jour",
    includedDishes: "Plats inclus",
    days: { lundi: 'lundi', mardi: 'mardi', mercredi: 'mercredi', jeudi: 'jeudi', vendredi: 'vendredi', samedi: 'samedi', dimanche: 'dimanche' }
  },
  en: {
    popular: "Popular",
    dineIn: "Dine in",
    takeaway: "Takeaway",
    closedToday: "Closed today",
    open: "Open",
    closed: "Closed",
    openingHours: "Opening hours",
    today: "Today",
    viewMenu: "View menu",
    allergens: "Allergens",
    youMightLike: "You might also like",
    orderMessage: "To order, please ask our staff",
    menuPrep: "Menu in preparation",
    menuPrepDesc: "This restaurant is preparing its menu. Come back soon!",
    poweredBy: "Powered by",
    specialOffer: "Special offer",
    viewOffer: "View special offer →",
    yourReviewMatters: "Your review matters!",
    leaveReview: "Leave a review",
    unavailable: "Unavailable",
    hours: "Hours",
    top: "Top",
    translating: "Translating...",
    dailySpecial: "Today's special",
    includedDishes: "Included dishes",
    days: { lundi: 'Monday', mardi: 'Tuesday', mercredi: 'Wednesday', jeudi: 'Thursday', vendredi: 'Friday', samedi: 'Saturday', dimanche: 'Sunday' }
  }
};

// Allergens labels per language
const ALLERGENS_LABELS_I18N = {
  fr: {
    gluten: "Gluten", crustaces: "Crustacés", oeufs: "Œufs", poisson: "Poisson",
    arachides: "Arachides", soja: "Soja", lait: "Lait", fruits_coque: "Fruits à coque",
    celeri: "Céleri", moutarde: "Moutarde", sesame: "Sésame", sulfites: "Sulfites",
    lupin: "Lupin", mollusques: "Mollusques"
  },
  en: {
    gluten: "Gluten", crustaces: "Crustaceans", oeufs: "Eggs", poisson: "Fish",
    arachides: "Peanuts", soja: "Soy", lait: "Milk", fruits_coque: "Tree nuts",
    celeri: "Celery", moutarde: "Mustard", sesame: "Sesame", sulfites: "Sulphites",
    lupin: "Lupin", mollusques: "Molluscs"
  }
};

// Allergens labels (kept for backward compat)
const ALLERGENS_LABELS = ALLERGENS_LABELS_I18N.fr;

// Helper to get current day in French
const getCurrentDay = () => {
  const days = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
  return days[new Date().getDay()];
};

// Helper to check if restaurant is open
const getOpenStatus = (openingHours) => {
  if (!openingHours || Object.keys(openingHours).length === 0) return null;
  
  const currentDay = getCurrentDay();
  const todayHours = openingHours[currentDay];
  
  if (!todayHours || todayHours.toLowerCase() === 'fermé') {
    return { isOpen: false, text: 'Fermé aujourd\'hui' };
  }
  
  return { isOpen: true, text: todayHours };
};

// Helper to create deeplinks for social media
const getSocialDeeplink = (url, platform) => {
  if (!url) return null;
  
  try {
    const urlObj = new URL(url);
    
    if (platform === 'instagram') {
      // Extract username from Instagram URL
      const pathParts = urlObj.pathname.split('/').filter(Boolean);
      const username = pathParts[0];
      if (username) {
        return {
          deeplink: `instagram://user?username=${username}`,
          fallback: url
        };
      }
    }
    
    if (platform === 'facebook') {
      // For Facebook, we use intent URL on Android and regular URL as fallback
      return {
        deeplink: `fb://facewebmodal/f?href=${encodeURIComponent(url)}`,
        fallback: url
      };
    }
  } catch (e) {
    // If URL parsing fails, return the original URL
  }
  
  return { deeplink: url, fallback: url };
};

// Handle social link click with deeplink attempt
const handleSocialClick = (e, url, platform) => {
  e.preventDefault();
  const links = getSocialDeeplink(url, platform);
  if (!links) return;
  
  // Try to open deeplink, fallback to regular URL
  const start = Date.now();
  const timeout = setTimeout(() => {
    // If we're still here after 1.5s, the app didn't open
    window.location.href = links.fallback;
  }, 1500);
  
  // Try the deeplink
  window.location.href = links.deeplink;
  
  // If app opens, clear the fallback timeout
  window.addEventListener('blur', () => {
    clearTimeout(timeout);
  }, { once: true });
};

// Theme configurations
const THEME_STYLES = {
  moderne: {
    background: 'bg-[#FAFAFA]',
    cardBg: 'bg-white',
    textPrimary: 'text-gray-900',
    textSecondary: 'text-gray-500',
    borderColor: 'border-gray-100',
    headerGradient: false,
    fontHeading: "'Inter', sans-serif",
    fontBody: "'Inter', sans-serif"
  },
  elegant: {
    background: 'bg-[#1A1A1A]',
    cardBg: 'bg-[#2D2D2D]',
    textPrimary: 'text-white',
    textSecondary: 'text-gray-400',
    borderColor: 'border-gray-700',
    headerGradient: true,
    fontHeading: "'Playfair Display', serif",
    fontBody: "'Lora', serif"
  },
  rustique: {
    background: 'bg-[#FDF5E6]',
    cardBg: 'bg-[#FFFAF0]',
    textPrimary: 'text-[#3E2723]',
    textSecondary: 'text-[#6D4C41]',
    borderColor: 'border-[#D7CCC8]',
    headerGradient: false,
    fontHeading: "'Merriweather', serif",
    fontBody: "'Open Sans', sans-serif"
  },
  ocean: {
    background: 'bg-[#F0F9FF]',
    cardBg: 'bg-white',
    textPrimary: 'text-[#0C4A6E]',
    textSecondary: 'text-[#0369A1]',
    borderColor: 'border-[#BAE6FD]',
    headerGradient: false,
    fontHeading: "'Poppins', sans-serif",
    fontBody: "'Poppins', sans-serif"
  },
  nature: {
    background: 'bg-[#F0FDF4]',
    cardBg: 'bg-white',
    textPrimary: 'text-[#14532D]',
    textSecondary: 'text-[#166534]',
    borderColor: 'border-[#BBF7D0]',
    headerGradient: false,
    fontHeading: "'Nunito', sans-serif",
    fontBody: "'Nunito', sans-serif"
  },
  neon: {
    background: 'bg-[#0F0F0F]',
    cardBg: 'bg-[#1A1A1A]',
    textPrimary: 'text-white',
    textSecondary: 'text-[#A1A1AA]',
    borderColor: 'border-[#27272A]',
    headerGradient: true,
    fontHeading: "'Space Grotesk', sans-serif",
    fontBody: "'Space Grotesk', sans-serif"
  },
  vintage: {
    background: 'bg-[#FFFBEB]',
    cardBg: 'bg-[#FEF3C7]',
    textPrimary: 'text-[#78350F]',
    textSecondary: 'text-[#92400E]',
    borderColor: 'border-[#FDE68A]',
    headerGradient: false,
    fontHeading: "'Libre Baskerville', serif",
    fontBody: "'Source Sans Pro', sans-serif"
  },
  minuit: {
    background: 'bg-[#030712]',
    cardBg: 'bg-[#111827]',
    textPrimary: 'text-[#F9FAFB]',
    textSecondary: 'text-[#9CA3AF]',
    borderColor: 'border-[#1F2937]',
    headerGradient: true,
    fontHeading: "'Outfit', sans-serif",
    fontBody: "'Outfit', sans-serif"
  },
  cafe: {
    background: 'bg-[#FEF7EE]',
    cardBg: 'bg-white',
    textPrimary: 'text-[#431407]',
    textSecondary: 'text-[#9A3412]',
    borderColor: 'border-[#FED7AA]',
    headerGradient: false,
    fontHeading: "'Josefin Sans', sans-serif",
    fontBody: "'Quicksand', sans-serif"
  },
  pastel: {
    background: 'bg-[#FDF2F8]',
    cardBg: 'bg-white',
    textPrimary: 'text-[#831843]',
    textSecondary: 'text-[#BE185D]',
    borderColor: 'border-[#FBCFE8]',
    headerGradient: false,
    fontHeading: "'Quicksand', sans-serif",
    fontBody: "'Quicksand', sans-serif"
  }
};

const PublicMenu = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const tableId = searchParams.get('table');
  const [tableLabel, setTableLabel] = useState('');
  const [orderingEnabled, setOrderingEnabled] = useState(false);
  const [orderType, setOrderType] = useState(tableId ? 'dine_in' : 'takeaway');
  const [paymentResult, setPaymentResult] = useState(null);

  // Handle Stripe payment return (?payment=success|cancelled)
  useEffect(() => {
    const payment = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    if (payment === 'cancelled') {
      setPaymentResult({ state: 'cancelled' });
    } else if (payment === 'success' && sessionId) {
      setPaymentResult({ state: 'checking' });
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const res = await axios.get(`${BACKEND_URL}/api/public/payments/status/${sessionId}`);
          if (res.data.payment_status === 'paid') {
            setPaymentResult({ state: 'paid', orderNumber: res.data.order_number, trackingToken: res.data.tracking_token });
            return;
          }
        } catch (e) { /* retry */ }
        if (attempts < 6) setTimeout(poll, 2000);
        else setPaymentResult({ state: 'timeout' });
      };
      poll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closePaymentModal = () => {
    setPaymentResult(null);
    window.history.replaceState({}, '', window.location.pathname);
  };
  const [menuData, setMenuData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [activeCategory, setActiveCategory] = useState(null);
  const [showPromoPopup, setShowPromoPopup] = useState(false);
  const [currentPromotion, setCurrentPromotion] = useState(null);
  const [currentPromoIndex, setCurrentPromoIndex] = useState(0);
  const [selectedDish, setSelectedDish] = useState(null);
  const [showDishModal, setShowDishModal] = useState(false);
  const [showHoursModal, setShowHoursModal] = useState(false);
  const [showReviewMenu, setShowReviewMenu] = useState(false);
  const [showReviewBanner, setShowReviewBanner] = useState(false);
  const [currentReviewMessageIndex, setCurrentReviewMessageIndex] = useState(0);
  const [lang, setLang] = useState(() => localStorage.getItem('zotmenu_lang') || 'fr');
  const [dishTranslations, setDishTranslations] = useState({});
  const [categoryTranslations, setCategoryTranslations] = useState({});
  const [promoTranslations, setPromoTranslations] = useState({});
  const [translating, setTranslating] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState(null); // null = auto or no menus
  
  const categoryRefs = useRef({});
  const navRef = useRef(null);
  const mainRef = useRef(null);

  // Review banner messages (rotating)
  const reviewMessages = lang === 'en' ? [
    { emoji: "⭐", text: "Enjoyed it? Share your experience!" },
    { emoji: "🙏", text: "A review = a big thank you!" },
    { emoji: "📝", text: "30 seconds to help us!" },
    { emoji: "💬", text: "Your opinion matters a lot!" },
    { emoji: "❤️", text: "Help other foodies discover us!" }
  ] : [
    { emoji: "⭐", text: "Satisfait ? Partagez votre expérience !" },
    { emoji: "🙏", text: "Un avis = un grand merci !" },
    { emoji: "📝", text: "30 secondes pour nous aider !" },
    { emoji: "💬", text: "Votre avis compte énormément !" },
    { emoji: "❤️", text: "Aidez d'autres gourmands à nous découvrir !" }
  ];

  // Translation helpers
  const t = UI_TEXTS[lang] || UI_TEXTS.fr;
  const allergensLabels = ALLERGENS_LABELS_I18N[lang] || ALLERGENS_LABELS_I18N.fr;

  const getDishName = (dish) => {
    if (lang === 'fr' || !dishTranslations[dish.id]) return dish.name;
    return dishTranslations[dish.id]?.name || dish.name;
  };

  const getDishDesc = (dish) => {
    const original = dish.description || '';
    if (lang === 'fr' || !dishTranslations[dish.id]) return original;
    return dishTranslations[dish.id]?.description || original;
  };

  const getCatNameTranslated = (category) => {
    if (lang === 'fr' || !categoryTranslations[category.id]) return category.name;
    return categoryTranslations[category.id] || category.name;
  };

  const getPromoText = (promo, field) => {
    const original = promo[field] || '';
    if (lang === 'fr' || !promoTranslations[promo.id]) return original;
    return promoTranslations[promo.id]?.[field] || original;
  };

  // Fetch translations when language changes to English
  useEffect(() => {
    if (lang === 'en' && menuData && Object.keys(dishTranslations).length === 0) {
      fetchTranslations();
    }
  }, [lang, menuData]);

  const toggleLang = () => {
    const newLang = lang === 'fr' ? 'en' : 'fr';
    setLang(newLang);
    localStorage.setItem('zotmenu_lang', newLang);
  };

  const fetchTranslations = async () => {
    setTranslating(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/menu/${slug}/translate/en`);
      if (response.data?.dishes) {
        setDishTranslations(response.data.dishes);
      }
      if (response.data?.categories) {
        setCategoryTranslations(response.data.categories);
      }
      if (response.data?.promotions) {
        setPromoTranslations(response.data.promotions);
      }
    } catch (e) {
      console.error('Translation error:', e);
    } finally {
      setTranslating(false);
    }
  };

  useEffect(() => {
    fetchMenu();
  }, [slug]);

  // Auto-select menu based on current day/time
  useEffect(() => {
    if (!menuData?.menus?.length) return;
    const now = new Date();
    const dayNames = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
    const currentDay = dayNames[now.getDay()];
    const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    
    // Find the menu matching current day + time
    for (const menu of menuData.menus) {
      for (const slot of (menu.time_slots || [])) {
        if (slot.days?.includes(currentDay) && currentTime >= slot.start_hour && currentTime <= slot.end_hour) {
          setSelectedMenu(menu.id);
          return;
        }
      }
    }
    // If no match, default to first menu
    setSelectedMenu(menuData.menus[0].id);
  }, [menuData?.menus]);

  useEffect(() => {
    // Show popup after menu loads if there are promotions
    if (menuData?.promotions?.length > 0) {
      const dismissedPromos = JSON.parse(sessionStorage.getItem(`dismissed_promos_${slug}`) || '[]');
      const unshownPromo = menuData.promotions.find(p => !dismissedPromos.includes(p.id));
      
      if (unshownPromo) {
        const timer = setTimeout(() => {
          setCurrentPromotion(unshownPromo);
          setShowPromoPopup(true);
          trackEvent('promo_popup_view', { promo_id: unshownPromo.id });
        }, 1500);
        return () => clearTimeout(timer);
      }
    }
  }, [menuData, slug]);

  // Auto-slide promotions banner every 4 seconds
  useEffect(() => {
    if (menuData?.promotions?.length > 1) {
      const interval = setInterval(() => {
        setCurrentPromoIndex(prev => (prev + 1) % menuData.promotions.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [menuData?.promotions?.length]);

  // Review banner animation: 5s visible, 15s hidden, starts after 20s
  useEffect(() => {
    const hasReviewLinks = menuData?.restaurant?.google_review_link || menuData?.restaurant?.tripadvisor_review_link;
    
    if (!hasReviewLinks) {
      return;
    }

    let showTimer;
    let hideTimer;
    let isActive = true;

    const startCycle = () => {
      if (!isActive) return;
      
      // Show for 5 seconds
      showTimer = setTimeout(() => {
        if (!isActive) return;
        setShowReviewBanner(false);
        
        // Hide for 15 seconds, then show again with new message
        hideTimer = setTimeout(() => {
          if (!isActive) return;
          setCurrentReviewMessageIndex(i => (i + 1) % reviewMessages.length);
          setShowReviewBanner(true);
          startCycle(); // Restart the cycle
        }, 15000);
      }, 5000);
    };

    // Start cycle after initial 20s delay
    const initialTimer = setTimeout(() => {
      if (!isActive) return;
      setShowReviewBanner(true);
      startCycle();
    }, 20000);

    return () => {
      isActive = false;
      clearTimeout(initialTimer);
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, [menuData, slug, reviewMessages.length]);

  const handleReviewBannerClick = () => {
    trackEvent('review_banner_click');
    setShowReviewMenu(true);
  };

  // Track scroll to update active category
  useEffect(() => {
    if (!menuData?.categories?.length) return;

    const handleScroll = () => {
      const scrollPosition = window.scrollY + 180; // Offset for sticky headers
      
      let currentCategory = null;
      
      for (const category of menuData.categories) {
        const element = categoryRefs.current[category.id];
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            currentCategory = category.id;
            break;
          }
        }
      }
      
      if (currentCategory && currentCategory !== activeCategory) {
        setActiveCategory(currentCategory);
        // Scroll nav to show active category
        scrollNavToCategory(currentCategory);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [menuData, activeCategory]);

  const scrollNavToCategory = (categoryId) => {
    if (!navRef.current) return;
    const navButton = navRef.current.querySelector(`[data-category="${categoryId}"]`);
    if (navButton) {
      navButton.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  };

  const scrollToCategory = (categoryId) => {
    const element = categoryRefs.current[categoryId];
    if (element) {
      const yOffset = -160; // Account for sticky headers
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
      setActiveCategory(categoryId);
      
      // Ensure category is expanded
      setExpandedCategories(prev => ({ ...prev, [categoryId]: true }));
    }
  };

  const fetchMenu = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/menu/${slug}`);
      setMenuData(response.data);
      // Check ordering
      const restData = response.data.restaurant;
      setOrderingEnabled(!!restData?.ordering_enabled);
      // Fetch table info if table param
      if (tableId) {
        try {
          const tableRes = await axios.get(`${BACKEND_URL}/api/public/table/${tableId}`);
          setTableLabel(tableRes.data.table_label);
          if (tableRes.data.ordering_enabled) setOrderingEnabled(true);
        } catch {}
      }
      // Expand all categories by default
      const expanded = {};
      response.data.categories.forEach(cat => {
        expanded[cat.id] = true;
      });
      setExpandedCategories(expanded);
      // Set first category as active
      if (response.data.categories.length > 0) {
        setActiveCategory(response.data.categories[0].id);
      }
    } catch (err) {
      setError(err.response?.status === 404 ? 'Menu non trouvé' : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const dismissPopup = () => {
    if (currentPromotion) {
      const dismissedPromos = JSON.parse(sessionStorage.getItem(`dismissed_promos_${slug}`) || '[]');
      dismissedPromos.push(currentPromotion.id);
      sessionStorage.setItem(`dismissed_promos_${slug}`, JSON.stringify(dismissedPromos));
    }
    setShowPromoPopup(false);
  };

  // Generate unique session ID
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const sessionStartRef = useRef(false);
  const visitedCategories = useRef([]);

  const trackEvent = async (eventType, extraData = {}) => {
    try {
      await axios.post(`${BACKEND_URL}/api/public/track`, {
        event_type: eventType,
        restaurant_id: menuData?.restaurant?.id,
        session_id: sessionId,
        ...extraData
      });
    } catch (e) {}
  };

  const trackView = async (dishId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/public/track`, {
        event_type: 'view',
        restaurant_id: menuData?.restaurant?.id,
        dish_id: dishId,
        session_id: sessionId
      });
    } catch (e) {}
  };

  const trackClick = async (dishId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/public/track`, {
        event_type: 'click',
        restaurant_id: menuData?.restaurant?.id,
        dish_id: dishId,
        session_id: sessionId
      });
    } catch (e) {}
  };

  // Track session start/end
  useEffect(() => {
    if (menuData?.restaurant?.id && !sessionStartRef.current) {
      sessionStartRef.current = true;
      trackEvent('session_start');
      
      // Track session end on page unload
      const handleUnload = () => {
        // Use sendBeacon for reliable tracking on page close
        const data = JSON.stringify({
          event_type: 'session_end',
          restaurant_id: menuData?.restaurant?.id,
          session_id: sessionId
        });
        navigator.sendBeacon(`${BACKEND_URL}/api/public/track`, data);
      };
      
      window.addEventListener('beforeunload', handleUnload);
      return () => window.removeEventListener('beforeunload', handleUnload);
    }
  }, [menuData?.restaurant?.id, sessionId]);

  // Track category view when active category changes
  useEffect(() => {
    if (activeCategory && menuData?.restaurant?.id) {
      const categoryName = menuData?.categories?.find(c => c.id === activeCategory)?.name;
      if (categoryName && !visitedCategories.current.includes(activeCategory)) {
        visitedCategories.current.push(activeCategory);
        trackEvent('category_view', { category_name: categoryName, category_id: activeCategory });
      }
    }
  }, [activeCategory, menuData]);

  // Track review clicks
  const trackReviewClick = (platform) => {
    trackEvent(`review_click_${platform}`);
    // Also mark in sessionStorage that user clicked a review link
    sessionStorage.setItem(`review_clicked_${slug}`, 'true');
  };

  // Track promotion events
  const trackPromoView = (promoId) => {
    trackEvent('promo_popup_view', { promo_id: promoId });
  };

  const trackPromoClick = (promoId) => {
    trackEvent('promo_popup_click', { promo_id: promoId });
  };

  const trackBannerPromoView = (promoId) => {
    trackEvent('promo_view', { promo_id: promoId });
  };

  const trackBannerPromoClick = (promoId) => {
    trackEvent('promo_click', { promo_id: promoId });
  };

  const openDishModal = (dish) => {
    setSelectedDish(dish);
    setShowDishModal(true);
    trackClick(dish.id);
  };

  const closeDishModal = () => {
    setShowDishModal(false);
    setSelectedDish(null);
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const getDishesForCategory = (categoryId) => {
    const categoryDishes = menuData?.dishes?.filter(d => d.category_id === categoryId) || [];
    // If multi-menu active, filter by dish-level menu_ids too
    if (!menuData?.menus?.length || !selectedMenu) return categoryDishes;
    return categoryDishes.filter(d => {
      const dishMenuIds = d.menu_ids || [];
      if (dishMenuIds.length === 0) return true;
      return dishMenuIds.includes(selectedMenu);
    });
  };

  const getCategoryName = (categoryId) => {
    const category = menuData?.categories?.find(c => c.id === categoryId);
    if (!category) return '';
    return getCatNameTranslated(category);
  };

  const getSuggestedDishes = (currentDish) => {
    if (!menuData?.dishes) return [];
    
    // First, use manually defined upsells if available
    if (currentDish.upsells && currentDish.upsells.length > 0) {
      const upsellDishes = currentDish.upsells
        .map(id => menuData.dishes.find(d => d.id === id))
        .filter(d => d && d.available);
      if (upsellDishes.length > 0) return upsellDishes.slice(0, 3);
    }
    
    // Fallback: suggest dishes from different categories (complementary items)
    return menuData.dishes
      .filter(d => d.id !== currentDish.id && d.available && d.category_id !== currentDish.category_id)
      .sort((a, b) => (b.views || 0) - (a.views || 0))
      .slice(0, 3);
  };

  const primaryColor = menuData?.restaurant?.primary_color || '#F97316';
  const themeKey = menuData?.restaurant?.theme || 'moderne';
  const theme = THEME_STYLES[themeKey] || THEME_STYLES.moderne;
  const themeConfig = menuData?.restaurant?.theme_config || {};
  const displayMode = menuData?.restaurant?.menu_display_mode || 'carousel';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-lg mx-auto space-y-4">
          <Skeleton className="h-24 rounded-2xl" />
          <Skeleton className="h-12 rounded-full" />
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="text-6xl mb-4">🍽️</div>
            <h1 className="font-heading text-2xl font-bold text-gray-900 mb-2">{error}</h1>
            <p className="text-gray-500">Ce menu n'existe pas ou n'est plus disponible.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Filter categories that have dishes (and belong to selected menu if multi-menu)
  const hasMenus = menuData?.menus?.length > 0;
  const categoriesForMenu = hasMenus && selectedMenu
    ? menuData?.categories?.filter(cat => {
        const menuIds = cat.menu_ids || [];
        return menuIds.length === 0 || menuIds.includes(selectedMenu);
      })
    : menuData?.categories;

  const categoriesWithDishes = categoriesForMenu?.filter(cat => 
    getDishesForCategory(cat.id).length > 0
  ) || [];

  return (
    <div 
      className={`min-h-screen ${theme.background}`} 
      data-testid="public-menu"
      style={{ 
        fontFamily: theme.fontBody,
        '--theme-primary': themeConfig.primary_color || primaryColor,
        '--theme-bg': themeConfig.background || '#FAFAFA',
        '--theme-card': themeConfig.card_bg || '#FFFFFF',
        '--theme-text': themeConfig.text_primary || '#111827',
        '--theme-text-secondary': themeConfig.text_secondary || '#6B7280'
      }}
    >
      {/* Dish Detail Modal */}
      {showDishModal && selectedDish && (
        <div 
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in"
          onClick={closeDishModal}
        >
          <div 
            className="w-full max-w-lg bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] animate-slide-up sm:animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeDishModal}
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/20 text-white hover:bg-black/40 transition-colors"
              data-testid="close-dish-modal"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="overflow-y-auto max-h-[90vh]">
              {selectedDish.photo_url ? (
                <div className="relative h-56 sm:h-64">
                  <img 
                    src={selectedDish.photo_url} 
                    alt={selectedDish.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                </div>
              ) : (
                <div 
                  className="h-32 flex items-center justify-center"
                  style={{ background: `linear-gradient(135deg, ${primaryColor}22 0%, ${primaryColor}11 100%)` }}
                >
                  <span className="text-6xl">🍽️</span>
                </div>
              )}

              <div className="p-6">
                <div className="flex items-center gap-2 mb-2">
                  <Badge 
                    variant="outline" 
                    className="text-xs font-normal"
                    style={{ borderColor: `${primaryColor}44`, color: primaryColor }}
                  >
                    {getCategoryName(selectedDish.category_id)}
                  </Badge>
                  {selectedDish.views > 10 && (
                    <Badge className="text-xs bg-orange-100 text-orange-600 border-0">
                      <Flame className="h-3 w-3 mr-1" />
                      {t.popular}
                    </Badge>
                  )}
                </div>

                <div className="flex items-start justify-between gap-4 mb-4">
                  <h2 className="font-heading text-2xl font-bold text-gray-900">
                    {getDishName(selectedDish)}
                  </h2>
                  <div className="text-right flex-shrink-0">
                    {selectedDish.price_takeaway != null ? (
                      <>
                        <div className="text-sm text-gray-500">{t.dineIn}</div>
                        <div 
                          className="text-2xl font-bold whitespace-nowrap"
                          style={{ color: primaryColor }}
                        >
                          {selectedDish.price.toFixed(2)}€
                        </div>
                        <div className="text-sm text-gray-500 mt-1">{t.takeaway}</div>
                        <div 
                          className="text-lg font-bold whitespace-nowrap"
                          style={{ color: primaryColor }}
                        >
                          {selectedDish.price_takeaway.toFixed(2)}€
                        </div>
                      </>
                    ) : (
                      <div 
                        className="text-2xl font-bold whitespace-nowrap"
                        style={{ color: primaryColor }}
                      >
                        {selectedDish.price.toFixed(2)}€
                      </div>
                    )}
                  </div>
                </div>

                {getDishDesc(selectedDish) && (
                  <p className="text-gray-600 leading-relaxed mb-4">
                    {getDishDesc(selectedDish)}
                  </p>
                )}

                {/* Allergens in modal */}
                {selectedDish.allergens && selectedDish.allergens.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                      <span className="font-semibold text-amber-800 text-sm">{t.allergens}</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {selectedDish.allergens.map((allergen) => (
                        <Badge 
                          key={allergen}
                          variant="outline" 
                          className="bg-white text-amber-700 border-amber-300 text-xs"
                        >
                          {allergensLabels[allergen] || allergen}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {getSuggestedDishes(selectedDish).length > 0 && (
                  <div className="border-t border-gray-100 pt-5 mt-2">
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Sparkles className="h-4 w-4" style={{ color: primaryColor }} />
                      {t.youMightLike}
                    </h3>
                    <div className="flex gap-3 overflow-x-auto pb-2 -mx-2 px-2">
                      {getSuggestedDishes(selectedDish).map((dish) => (
                        <div 
                          key={dish.id}
                          className="flex-shrink-0 w-28 cursor-pointer"
                          onClick={() => {
                            setSelectedDish(dish);
                            trackClick(dish.id);
                          }}
                        >
                          {dish.photo_url ? (
                            <img 
                              src={dish.photo_url} 
                              alt={dish.name}
                              className="w-full h-20 object-cover rounded-xl mb-2"
                            />
                          ) : (
                            <div className="w-full h-20 bg-gray-100 rounded-xl mb-2 flex items-center justify-center">
                              <span className="text-2xl">🍽️</span>
                            </div>
                          )}
                          <p className="text-xs font-medium text-gray-900 line-clamp-1">{getDishName(dish)}</p>
                          {dish.price_takeaway != null ? (
                            <>
                              <p className="text-[10px] text-gray-400">{t.dineIn}</p>
                              <p className="text-xs font-bold" style={{ color: primaryColor }}>{dish.price.toFixed(2)}€</p>
                              <p className="text-[10px] text-gray-400">{t.takeaway}</p>
                              <p className="text-[10px] font-bold" style={{ color: primaryColor }}>{dish.price_takeaway.toFixed(2)}€</p>
                            </>
                          ) : (
                            <p className="text-xs font-bold" style={{ color: primaryColor }}>{dish.price.toFixed(2)}€</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-6 p-4 bg-gray-50 rounded-xl text-center">
                  {orderingEnabled ? (
                    <button
                      onClick={() => { if (window.__menuCart) { window.__menuCart.addToCart(selectedDish); closeDishModal(); } }}
                      className="w-full py-3 rounded-full text-white font-semibold text-sm flex items-center justify-center gap-2 transition-transform hover:scale-[1.02]"
                      style={{ backgroundColor: primaryColor }}
                      data-testid="add-to-cart-modal-btn"
                    >
                      <ShoppingCart className="h-4 w-4" />
                      Ajouter au panier — {(orderType === 'takeaway' && selectedDish.price_takeaway != null ? selectedDish.price_takeaway : selectedDish.price).toFixed(2)}€
                      {selectedDish.price_takeaway != null && (
                        <span className="opacity-80 font-normal">· {orderType === 'takeaway' ? t.takeaway : t.dineIn}</span>
                      )}
                    </button>
                  ) : (
                    <p className="text-sm text-gray-500">{t.orderMessage}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Opening Hours Modal */}
      {showHoursModal && (
        <div 
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in"
          onClick={() => setShowHoursModal(false)}
        >
          <div 
            className="w-full max-w-sm bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden animate-slide-up sm:animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Clock className="h-5 w-5" style={{ color: primaryColor }} />
                  {t.openingHours}
                </h2>
                <button
                  onClick={() => setShowHoursModal(false)}
                  className="p-2 rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <div className="space-y-3">
                {['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'].map((day) => {
                  const hours = menuData?.restaurant?.opening_hours?.[day];
                  const isToday = getCurrentDay() === day;
                  const isClosed = !hours || hours.toLowerCase() === 'fermé';
                  
                  return (
                    <div 
                      key={day}
                      className={`flex items-center justify-between py-2 px-3 rounded-lg ${
                        isToday ? 'bg-gray-50' : ''
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {isToday && (
                          <div 
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: primaryColor }}
                          />
                        )}
                        <span className={`capitalize ${isToday ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
                          {t.days[day] || day}
                        </span>
                        {isToday && (
                          <span 
                            className="text-xs px-2 py-0.5 rounded-full"
                            style={{ backgroundColor: `${primaryColor}20`, color: primaryColor }}
                          >
                            {t.today}
                          </span>
                        )}
                      </div>
                      <span className={`text-sm ${isClosed ? 'text-red-500' : 'text-gray-900'} ${isToday ? 'font-semibold' : ''}`}>
                        {hours || 'Fermé'}
                      </span>
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 pt-4 border-t border-gray-100">
                <Button
                  onClick={() => setShowHoursModal(false)}
                  className="w-full h-12 rounded-full text-white font-semibold"
                  style={{ backgroundColor: primaryColor }}
                >
                  {t.viewMenu}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Promotion Popup with Navigation */}
      {showPromoPopup && currentPromotion && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
          onClick={dismissPopup}
        >
          <div 
            className="w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden animate-scale-in relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={dismissPopup}
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/20 text-white hover:bg-black/40 transition-colors"
              data-testid="close-promo-popup"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Header with gradient */}
            <div 
              className="pt-12 pb-6 px-6 relative"
              style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)` }}
            >
              {currentPromotion.image_url && (
                <div className="absolute inset-0 opacity-20">
                  <img 
                    src={currentPromotion.image_url} 
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-2 rounded-full bg-white/20">
                    <Gift className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-sm font-medium uppercase tracking-wider text-white/90">
                    {t.specialOffer} {menuData?.promotions?.length > 1 && `(${menuData.promotions.indexOf(currentPromotion) + 1}/${menuData.promotions.length})`}
                  </span>
                </div>
                <h2 
                  className="font-heading text-xl font-bold text-white leading-tight"
                  dangerouslySetInnerHTML={{ 
                    __html: getPromoText(currentPromotion, 'title')
                      .replace(/\*\*(.*?)\*\*/g, '$1')
                      .replace(/"/g, '')
                  }}
                />
                {currentPromotion.discount_text && (
                  <div className="mt-3 inline-block px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm">
                    <span 
                      className="text-white font-bold text-sm"
                      dangerouslySetInnerHTML={{ 
                        __html: getPromoText(currentPromotion, 'discount_text')
                          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                      }}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Content */}
            <div className="p-5">
              <div 
                className="text-gray-600 text-sm leading-relaxed mb-5 space-y-2"
                dangerouslySetInnerHTML={{ 
                  __html: getPromoText(currentPromotion, 'description')
                    .replace(/\*\*(.*?)\*\*/g, '<strong class="text-gray-900 font-semibold">$1</strong>')
                    .replace(/\n/g, '<br />')
                    .replace(/- /g, '• ')
                }}
              />

              {/* Navigation dots for multiple promos */}
              {menuData?.promotions?.length > 1 && (
                <div className="flex justify-center gap-2 mb-4">
                  {menuData.promotions.map((promo, index) => (
                    <button
                      key={promo.id}
                      className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                        currentPromotion.id === promo.id 
                          ? 'w-6' 
                          : 'hover:opacity-70'
                      }`}
                      style={{ 
                        backgroundColor: currentPromotion.id === promo.id ? primaryColor : '#d1d5db'
                      }}
                      onClick={() => setCurrentPromotion(promo)}
                    />
                  ))}
                </div>
              )}

              <Button
                onClick={() => {
                  trackEvent('promo_popup_click', { promo_id: currentPromotion.id });
                  dismissPopup();
                }}
                className="w-full h-11 rounded-full text-white font-semibold shadow-lg"
                style={{ backgroundColor: primaryColor }}
                data-testid="promo-cta"
              >
                {t.viewMenu}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header 
        className="sticky top-0 z-50 px-4 py-4"
        style={{ backgroundColor: primaryColor }}
      >
        <div className="max-w-lg mx-auto">
          <div className="flex items-center gap-3">
            {menuData?.restaurant?.logo_url ? (
              <img 
                src={menuData.restaurant.logo_url} 
                alt={menuData.restaurant.name}
                className="h-12 w-12 rounded-xl object-cover bg-white"
              />
            ) : (
              <div className="h-12 w-12 rounded-xl bg-white/20 flex items-center justify-center text-white text-xl font-bold">
                {menuData?.restaurant?.name?.charAt(0)}
              </div>
            )}
            <div className="text-white flex-1">
              <div className="flex items-center gap-2">
                <h1 className="font-heading font-bold text-lg">{menuData?.restaurant?.name}</h1>
                {(menuData?.restaurant?.halal || (menuData?.restaurant?.restaurant_categories || []).includes('Halal')) && (
                  <span className="bg-emerald-500 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full" data-testid="halal-badge">
                    Halal
                  </span>
                )}
              </div>
              {menuData?.restaurant?.description && (
                <p className="text-white/80 text-sm line-clamp-1">{menuData.restaurant.description}</p>
              )}
            </div>
            {/* Hours button */}
            {menuData?.restaurant?.opening_hours && Object.keys(menuData.restaurant.opening_hours).length > 0 && (
              <button
                onClick={() => setShowHoursModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/20 text-white text-sm hover:bg-white/30 transition-colors"
                data-testid="hours-btn"
              >
                <Clock className="h-4 w-4" />
                <span className="hidden sm:inline">{t.hours}</span>
              </button>
            )}
            {/* Language toggle */}
            <button
              onClick={toggleLang}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/20 text-white text-sm hover:bg-white/30 transition-colors"
              data-testid="lang-toggle"
              disabled={translating}
            >
              {translating ? (
                <span className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full" />
              ) : (
                <Globe className="h-4 w-4" />
              )}
              <span className="font-semibold">{lang === 'fr' ? '🇬🇧' : '🇫🇷'}</span>
            </button>
          </div>
        </div>
      </header>

      {/* Opening Hours Info Bar */}
      {menuData?.restaurant?.opening_hours && Object.keys(menuData.restaurant.opening_hours).length > 0 && (
        <div 
          className={`${theme.cardBg} ${theme.borderColor} border-b cursor-pointer`}
          onClick={() => setShowHoursModal(true)}
        >
          <div className="max-w-lg mx-auto px-4 py-2.5 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${getOpenStatus(menuData.restaurant.opening_hours)?.isOpen ? 'bg-emerald-500' : 'bg-red-500'}`} />
              <span className={`text-sm font-medium ${theme.textPrimary}`}>
                {getOpenStatus(menuData.restaurant.opening_hours)?.isOpen ? t.open : t.closed}
              </span>
              <span className={`text-sm ${theme.textSecondary}`}>
                · {lang === 'en' && !getOpenStatus(menuData.restaurant.opening_hours)?.isOpen ? t.closedToday : getOpenStatus(menuData.restaurant.opening_hours)?.text}
              </span>
            </div>
            <ChevronRight className={`h-4 w-4 ${theme.textSecondary}`} />
          </div>
        </div>
      )}

      {/* Contact Info Bar (Phone, Address & Social) */}
      {(menuData?.restaurant?.phone || menuData?.restaurant?.address || menuData?.restaurant?.facebook_url || menuData?.restaurant?.instagram_url) && (
        <div className={`${theme.cardBg} ${theme.borderColor} border-b`}>
          <div className="max-w-lg mx-auto px-4 py-2.5 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-4 flex-wrap">
              {menuData?.restaurant?.phone && (
                <a 
                  href={`tel:${menuData.restaurant.phone.replace(/\s/g, '')}`}
                  className={`flex items-center gap-1.5 text-sm ${theme.textPrimary} hover:opacity-70 transition-opacity`}
                  data-testid="phone-link"
                >
                  <Phone className="h-4 w-4" style={{ color: primaryColor }} />
                  <span>{menuData.restaurant.phone}</span>
                </a>
              )}
              {menuData?.restaurant?.address && (
                <a 
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(menuData.restaurant.address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center gap-1.5 text-sm ${theme.textPrimary} hover:opacity-70 transition-opacity`}
                  data-testid="address-link"
                >
                  <MapPin className="h-4 w-4" style={{ color: primaryColor }} />
                  <span className="line-clamp-1">{menuData.restaurant.address}</span>
                </a>
              )}
            </div>
            
            {/* Social Media Icons */}
            {(menuData?.restaurant?.facebook_url || menuData?.restaurant?.instagram_url) && (
              <div className="flex items-center gap-3">
                {menuData?.restaurant?.facebook_url && (
                  <a 
                    href={menuData.restaurant.facebook_url}
                    onClick={(e) => handleSocialClick(e, menuData.restaurant.facebook_url, 'facebook')}
                    className={`p-2 rounded-full hover:opacity-70 transition-all ${
                      ['elegant', 'neon', 'minuit'].includes(themeKey) ? 'bg-white/10' : 'bg-gray-100'
                    }`}
                    data-testid="facebook-link"
                    aria-label="Facebook"
                  >
                    <Facebook className="h-5 w-5 text-[#1877F2]" />
                  </a>
                )}
                {menuData?.restaurant?.instagram_url && (
                  <a 
                    href={menuData.restaurant.instagram_url}
                    onClick={(e) => handleSocialClick(e, menuData.restaurant.instagram_url, 'instagram')}
                    className={`p-2 rounded-full hover:opacity-70 transition-all ${
                      ['elegant', 'neon', 'minuit'].includes(themeKey) ? 'bg-white/10' : 'bg-gray-100'
                    }`}
                    data-testid="instagram-link"
                    aria-label="Instagram"
                  >
                    <Instagram className="h-5 w-5 text-[#E4405F]" />
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Multi-Menu Tabs */}
      {hasMenus && menuData.menus.length > 0 && (
        <div className={`${theme.cardBg} ${theme.borderColor} border-b`}>
          <div className="max-w-lg mx-auto">
            <div className="flex overflow-x-auto scrollbar-hide py-2 px-4 gap-2">
              {menuData.menus.map((menu) => (
                <button
                  key={menu.id}
                  onClick={() => setSelectedMenu(menu.id)}
                  className={`flex-shrink-0 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all whitespace-nowrap ${
                    selectedMenu === menu.id
                      ? `text-white shadow-md`
                      : ['elegant', 'neon', 'minuit'].includes(themeKey)
                        ? 'bg-white/10 text-gray-300 hover:bg-white/20'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  style={selectedMenu === menu.id ? { backgroundColor: themeConfig.primary_color || primaryColor } : {}}
                  data-testid={`menu-tab-${menu.id}`}
                >
                  {lang === 'fr' ? menu.name : (menu.name)}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Sticky Category Navigation */}
      {categoriesWithDishes.length > 1 && (
        <nav 
          className={`sticky z-40 ${theme.cardBg} ${theme.borderColor} border-b shadow-sm ${
            menuData?.restaurant?.opening_hours && Object.keys(menuData.restaurant.opening_hours).length > 0 
              ? 'top-[72px]' 
              : 'top-[72px]'
          }`}
          ref={navRef}
        >
          <div className="max-w-lg mx-auto">
            <div className="flex overflow-x-auto scrollbar-hide py-2 px-4 gap-2">
              {categoriesWithDishes.map((category) => {
                const isActive = activeCategory === category.id;
                const dishCount = getDishesForCategory(category.id).length;
                
                return (
                  <button
                    key={category.id}
                    data-category={category.id}
                    onClick={() => scrollToCategory(category.id)}
                    className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                      isActive 
                        ? 'text-white shadow-md' 
                        : ['elegant', 'neon', 'minuit'].includes(themeKey) 
                          ? 'bg-white/10 text-gray-300 hover:bg-white/20'
                          : 'bg-black/5 text-gray-600 hover:bg-black/10'
                    }`}
                    style={isActive ? { backgroundColor: themeConfig.primary_color || primaryColor } : {}}
                    data-testid={`nav-category-${category.id}`}
                  >
                    {getCatNameTranslated(category)}
                    <span className={`ml-1.5 text-xs ${isActive ? 'text-white/80' : theme.textSecondary}`}>
                      ({dishCount})
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </nav>
      )}

      <main className="max-w-lg mx-auto px-4 py-6 space-y-6" ref={mainRef}>
        {/* Active Promotions Slider */}
        {menuData?.promotions?.length > 0 && (
          <div className="relative">
            <div 
              className="p-4 rounded-2xl text-white cursor-pointer active:scale-[0.98] transition-all duration-300"
              style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}cc 100%)` }}
              onClick={() => {
                const promo = menuData.promotions[currentPromoIndex];
                trackEvent('promo_click', { promo_id: promo.id });
                setCurrentPromotion(promo);
                setShowPromoPopup(true);
              }}
            >
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-full bg-white/20 flex-shrink-0 mt-0.5">
                  <Gift className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-base leading-tight mb-2 transition-opacity duration-300">
                    {getPromoText(menuData.promotions[currentPromoIndex], 'title').replace(/\*\*/g, '').replace(/"/g, '')}
                  </p>
                  {menuData.promotions[currentPromoIndex].discount_text && (
                    <div className="bg-white/15 backdrop-blur-sm rounded-lg px-3 py-2 mb-2">
                      <p className="text-sm leading-relaxed">
                        {getPromoText(menuData.promotions[currentPromoIndex], 'discount_text').replace(/\*\*/g, '')}
                      </p>
                    </div>
                  )}
                  <p className="text-sm text-white/80">{t.viewOffer}</p>
                </div>
              </div>
            </div>
            
            {/* Pagination dots */}
            {menuData.promotions.length > 1 && (
              <div className="flex justify-center gap-2 mt-3">
                {menuData.promotions.map((_, index) => (
                  <button
                    key={index}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      index === currentPromoIndex 
                        ? 'w-6 bg-orange-500' 
                        : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentPromoIndex(index);
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Daily Special Banner */}
        {menuData?.daily_special && (
          <section data-testid="daily-special-banner">
            <div 
              className="rounded-2xl overflow-hidden border-2"
              style={{ borderColor: `${primaryColor}40` }}
            >
              <div 
                className="px-4 py-3 flex items-center gap-2"
                style={{ backgroundColor: `${primaryColor}12` }}
              >
                <UtensilsCrossed className="h-5 w-5" style={{ color: primaryColor }} />
                <h2 className={`font-heading font-bold text-base ${theme.textPrimary}`} style={{ fontFamily: theme.fontHeading }}>
                  {t.dailySpecial}
                </h2>
                {menuData.daily_special.price && (
                  <span 
                    className="ml-auto font-bold text-lg"
                    style={{ color: primaryColor }}
                  >
                    {menuData.daily_special.price.toFixed(2)}€
                  </span>
                )}
              </div>
              <div className={`px-4 py-3 ${theme.cardBg}`}>
                <h3 className={`font-semibold text-base ${theme.textPrimary} mb-1`}>
                  {menuData.daily_special.title}
                </h3>
                {menuData.daily_special.description && (
                  <p className={`text-sm ${theme.textSecondary} mb-3`}>
                    {menuData.daily_special.description}
                  </p>
                )}
                {menuData.daily_special.dishes?.length > 0 && (
                  <div>
                    <p className={`text-xs font-medium ${theme.textSecondary} mb-2`}>{t.includedDishes}</p>
                    <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                      {menuData.daily_special.dishes.map((dish) => (
                        <div 
                          key={dish.id}
                          className="flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg"
                          style={{ backgroundColor: `${primaryColor}08`, border: `1px solid ${primaryColor}20` }}
                        >
                          {dish.photo_url && (
                            <img src={dish.photo_url} alt={dish.name} className="w-8 h-8 rounded-lg object-cover" />
                          )}
                          <div>
                            <p className={`text-sm font-medium ${theme.textPrimary}`}>{getDishName(dish)}</p>
                            <p className="text-xs" style={{ color: primaryColor }}>{dish.price?.toFixed(2)}€</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Popular Section */}
        {menuData?.popular_dishes?.length > 0 && (
          <section>
            <h2 className={`font-heading font-semibold text-lg ${theme.textPrimary} mb-3 flex items-center gap-2`} style={{ fontFamily: theme.fontHeading }}>
              <Flame className="h-5 w-5 text-orange-500" />
              {t.popular}
            </h2>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
              {menuData.popular_dishes.filter(d => d.available !== false).slice(0, 5).map((dish) => (
                <div 
                  key={dish.id}
                  className="flex-shrink-0 w-36 cursor-pointer active:scale-[0.97] transition-transform"
                  onClick={() => openDishModal(dish)}
                >
                  <Card className={`border-0 shadow-soft overflow-hidden ${theme.cardBg}`}>
                    {dish.photo_url ? (
                      <img 
                        src={dish.photo_url} 
                        alt={dish.name}
                        className="w-full h-24 object-cover"
                      />
                    ) : (
                      <div className={`w-full h-24 ${['elegant', 'neon', 'minuit'].includes(themeKey) ? 'bg-white/10' : 'bg-gray-100'} flex items-center justify-center`}>
                        <span className="text-3xl">🍽️</span>
                      </div>
                    )}
                    <CardContent className="p-3">
                      <p className={`font-medium text-sm ${theme.textPrimary} line-clamp-1`}>{getDishName(dish)}</p>
                      {dish.price_takeaway != null ? (
                        <>
                          <p className={`text-[10px] ${theme.textSecondary}`}>{t.dineIn}</p>
                          <p className="font-bold text-sm" style={{ color: themeConfig.primary_color || primaryColor }}>{dish.price.toFixed(2)}€</p>
                          <p className={`text-[10px] ${theme.textSecondary}`}>{t.takeaway} {dish.price_takeaway.toFixed(2)}€</p>
                        </>
                      ) : (
                        <p className="font-bold text-sm" style={{ color: themeConfig.primary_color || primaryColor }}>{dish.price.toFixed(2)}€</p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Menu Categories */}
        <div className="space-y-6">
          {categoriesWithDishes.map((category) => {
            const dishes = getDishesForCategory(category.id);
            const isExpanded = expandedCategories[category.id];
            
            return (
              <section 
                key={category.id} 
                ref={el => categoryRefs.current[category.id] = el}
                id={`category-${category.id}`}
                className="scroll-mt-40"
              >
                <button
                  onClick={() => toggleCategory(category.id)}
                  className={`w-full flex items-center justify-between py-3 mb-2 sticky top-[120px] ${theme.background} z-10`}
                >
                  <h2 className={`font-heading font-bold text-xl ${theme.textPrimary} flex items-center gap-2`} style={{ fontFamily: theme.fontHeading }}>
                    {getCatNameTranslated(category)}
                    <span 
                      className="text-sm font-normal px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: `${themeConfig.primary_color || primaryColor}15`, color: themeConfig.primary_color || primaryColor }}
                    >
                      {dishes.length}
                    </span>
                  </h2>
                  {isExpanded ? (
                    <ChevronUp className={`h-5 w-5 ${theme.textSecondary}`} />
                  ) : (
                    <ChevronDown className={`h-5 w-5 ${theme.textSecondary}`} />
                  )}
                </button>
                
                {isExpanded && displayMode === 'carousel' && (
                  <div className="relative -mx-4">
                    <div 
                      className="flex gap-3 overflow-x-auto pb-3 px-4 snap-x snap-mandatory scrollbar-hide"
                      style={{ WebkitOverflowScrolling: 'touch' }}
                      data-testid={`carousel-${category.id}`}
                    >
                      {dishes.map((dish) => (
                        <div
                          key={dish.id}
                          className={`flex-shrink-0 snap-start rounded-xl overflow-hidden ${dish.available !== false ? 'cursor-pointer active:scale-[0.97]' : 'opacity-60'} transition-transform`}
                          style={{ width: 'calc(44vw - 12px)', maxWidth: '200px', minWidth: '155px' }}
                          onClick={() => dish.available !== false && openDishModal(dish)}
                          data-testid={`dish-${dish.id}`}
                        >
                          <Card className={`border-0 shadow-md overflow-hidden h-full ${theme.cardBg}`}>
                            <CardContent className="p-0 h-full flex flex-col">
                              <div className={`relative w-full h-[110px] sm:h-[130px] ${dish.available === false ? 'grayscale' : ''}`}>
                                {dish.photo_url ? (
                                  <img src={dish.photo_url} alt={dish.name} className="w-full h-full object-cover" onLoad={() => trackView(dish.id)} />
                                ) : (
                                  <div className={`w-full h-full ${['elegant', 'neon', 'minuit'].includes(themeKey) ? 'bg-white/10' : 'bg-gradient-to-br from-gray-100 to-gray-200'} flex items-center justify-center`}>
                                    <span className="text-4xl">🍽️</span>
                                  </div>
                                )}
                                {dish.available === false && (
                                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                    <Badge className="bg-red-500 text-white text-xs">{t.unavailable}</Badge>
                                  </div>
                                )}
                                {dish.is_popular && dish.available !== false && (
                                  <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-white text-xs font-medium flex items-center gap-1" style={{ backgroundColor: themeConfig.primary_color || primaryColor }}>
                                    <Flame className="h-3 w-3" /> {t.popular}
                                  </div>
                                )}
                              </div>
                              <div className="p-2.5 flex flex-col flex-1">
                                <h3 className={`font-semibold text-sm leading-tight ${theme.textPrimary} line-clamp-2 ${dish.available === false ? 'line-through' : ''}`}>{getDishName(dish)}</h3>
                                <div className="flex items-center justify-between mt-auto pt-1.5">
                                  <div>
                                    {dish.price_takeaway != null ? (
                                      <>
                                        <span className={`text-[10px] ${theme.textSecondary} ${dish.available === false ? 'line-through opacity-50' : ''}`}>{t.dineIn} </span>
                                        <span className={`font-bold text-sm ${dish.available === false ? 'line-through opacity-50' : ''}`} style={{ color: themeConfig.primary_color || primaryColor }}>{dish.price.toFixed(2)}€</span>
                                        <span className={`block text-[10px] ${theme.textSecondary} ${dish.available === false ? 'line-through opacity-50' : ''}`}>{t.takeaway} {dish.price_takeaway.toFixed(2)}€</span>
                                      </>
                                    ) : (
                                      <span className={`font-bold text-sm ${dish.available === false ? 'line-through opacity-50' : ''}`} style={{ color: themeConfig.primary_color || primaryColor }}>{dish.price.toFixed(2)}€</span>
                                    )}
                                  </div>
                                  {dish.available !== false && (
                                    <span className="w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: `${themeConfig.primary_color || primaryColor}15` }}>
                                      <ChevronRight className="h-4 w-4" style={{ color: themeConfig.primary_color || primaryColor }} />
                                    </span>
                                  )}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        </div>
                      ))}
                    </div>
                    {dishes.length > 2 && (
                      <div className="absolute right-0 top-0 bottom-3 w-8 pointer-events-none" style={{ background: `linear-gradient(to right, transparent, ${['elegant', 'neon', 'minuit'].includes(themeKey) ? '#1a1a2e' : '#f9fafb'})` }} />
                    )}
                  </div>
                )}

                {isExpanded && displayMode === 'list' && (
                  <div className="space-y-2" data-testid={`list-${category.id}`}>
                    {dishes.map((dish) => (
                      <Card 
                        key={dish.id}
                        className={`border-0 shadow-sm overflow-hidden ${dish.available !== false ? 'cursor-pointer active:scale-[0.99]' : 'opacity-60'} transition-transform ${theme.cardBg}`}
                        onClick={() => dish.available !== false && openDishModal(dish)}
                        data-testid={`dish-${dish.id}`}
                      >
                        <CardContent className="p-0">
                          <div className="flex">
                            <div className="flex-1 p-3 flex flex-col justify-center">
                              <div className="flex items-center gap-1.5 mb-1">
                                {dish.is_popular && dish.available !== false && (
                                  <span className="px-1.5 py-0.5 rounded-full text-white text-[10px] font-medium flex items-center gap-0.5" style={{ backgroundColor: themeConfig.primary_color || primaryColor }}>
                                    <Flame className="h-2.5 w-2.5" /> Top
                                  </span>
                                )}
                                {dish.available === false && <Badge className="bg-red-500 text-white text-[10px]">{t.unavailable}</Badge>}
                              </div>
                              <h3 className={`font-semibold text-sm ${theme.textPrimary} ${dish.available === false ? 'line-through' : ''}`}>{getDishName(dish)}</h3>
                              {getDishDesc(dish) && <p className={`text-xs ${theme.textSecondary} mt-0.5 line-clamp-2`}>{getDishDesc(dish)}</p>}
                              <div className="mt-1.5">
                                {dish.price_takeaway != null ? (
                                  <>
                                    <p className={`text-[10px] ${theme.textSecondary} ${dish.available === false ? 'line-through opacity-50' : ''}`}>{t.dineIn}</p>
                                    <p className={`font-bold text-sm ${dish.available === false ? 'line-through opacity-50' : ''}`} style={{ color: themeConfig.primary_color || primaryColor }}>{dish.price.toFixed(2)}€</p>
                                    <p className={`text-[10px] ${theme.textSecondary} ${dish.available === false ? 'line-through opacity-50' : ''}`}>{t.takeaway} {dish.price_takeaway.toFixed(2)}€</p>
                                  </>
                                ) : (
                                  <p className={`font-bold text-sm ${dish.available === false ? 'line-through opacity-50' : ''}`} style={{ color: themeConfig.primary_color || primaryColor }}>{dish.price.toFixed(2)}€</p>
                                )}
                              </div>
                            </div>
                            <div className={`w-24 h-24 flex-shrink-0 ${dish.available === false ? 'grayscale' : ''}`}>
                              {dish.photo_url ? (
                                <img src={dish.photo_url} alt={dish.name} className="w-full h-full object-cover rounded-r-lg" onLoad={() => trackView(dish.id)} />
                              ) : (
                                <div className={`w-full h-full ${['elegant', 'neon', 'minuit'].includes(themeKey) ? 'bg-white/10' : 'bg-gray-100'} flex items-center justify-center rounded-r-lg`}>
                                  <span className="text-2xl">🍽️</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </div>

        {/* Empty state */}
        {menuData?.dishes?.length === 0 && (
          <Card className={`border-0 shadow-soft ${theme.cardBg}`}>
            <CardContent className="p-8 text-center">
              <div className="text-5xl mb-4">📋</div>
              <h3 className={`font-heading font-semibold text-lg ${theme.textPrimary} mb-2`}>
                {t.menuPrep}
              </h3>
              <p className={theme.textSecondary}>
                {t.menuPrepDesc}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <footer className="pt-8 pb-20 text-center">
          <p className={`text-xs ${theme.textSecondary}`}>
            {t.poweredBy} <span className="font-semibold" style={{ color: themeConfig.primary_color || primaryColor }}>ZOTMENU</span>
          </p>
        </footer>
      </main>

      {/* Review Banner - Animated slide-up banner */}
      {(menuData?.restaurant?.google_review_link || menuData?.restaurant?.tripadvisor_review_link) && (
        <div 
          className={`fixed bottom-0 left-0 right-0 z-40 transition-all duration-500 ease-out ${
            showReviewBanner ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
          }`}
          style={{ pointerEvents: showReviewBanner ? 'auto' : 'none' }}
          data-testid="review-banner"
        >
          <div 
            className="mx-4 mb-4 rounded-2xl shadow-lg overflow-hidden"
            style={{ 
              backgroundColor: themeConfig.primary_color || primaryColor,
              boxShadow: `0 -4px 20px ${themeConfig.primary_color || primaryColor}30`,
              marginRight: '5rem' // Leave space for floating button
            }}
          >
            <button
              onClick={handleReviewBannerClick}
              className="w-full flex items-center gap-3 px-4 py-3 text-white text-left"
              data-testid="review-banner-cta"
            >
              <span className="text-2xl animate-pulse flex-shrink-0">
                {reviewMessages[currentReviewMessageIndex].emoji}
              </span>
              <span className="font-medium text-sm sm:text-base pr-2">
                {reviewMessages[currentReviewMessageIndex].text}
              </span>
            </button>
          </div>
        </div>
      )}

      {/* Floating Review Button */}
      {(menuData?.restaurant?.google_review_link || menuData?.restaurant?.tripadvisor_review_link) && (
        <div className="fixed bottom-6 right-6 z-50" data-testid="review-floating-btn">
          {/* Review Menu Popup */}
          {showReviewMenu && (
            <div 
              className="absolute bottom-16 right-0 mb-2 animate-scale-in"
              onClick={(e) => e.stopPropagation()}
            >
              <div className={`${theme.cardBg} rounded-2xl shadow-2xl p-4 min-w-[200px] border ${theme.borderColor}`}>
                <p className={`text-sm font-semibold ${theme.textPrimary} mb-3`}>
                  {t.yourReviewMatters}
                </p>
                <div className="space-y-2">
                  {menuData?.restaurant?.google_review_link && (
                    <a
                      href={menuData.restaurant.google_review_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackReviewClick('google')}
                      className="flex items-center gap-3 p-3 rounded-xl bg-white hover:bg-gray-50 border border-gray-200 transition-all hover:scale-[1.02] active:scale-[0.98]"
                      data-testid="google-review-link"
                    >
                      <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-sm">
                        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none">
                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                        </svg>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 text-sm">Google</p>
                        <p className="text-xs text-gray-500">{t.leaveReview}</p>
                      </div>
                    </a>
                  )}
                  {menuData?.restaurant?.tripadvisor_review_link && (
                    <a
                      href={menuData.restaurant.tripadvisor_review_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackReviewClick('tripadvisor')}
                      className="flex items-center gap-3 p-3 rounded-xl bg-white hover:bg-gray-50 border border-gray-200 transition-all hover:scale-[1.02] active:scale-[0.98]"
                      data-testid="tripadvisor-review-link"
                    >
                      <div className="w-8 h-8 rounded-full bg-[#34E0A1] flex items-center justify-center shadow-sm">
                        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="white">
                          <circle cx="8" cy="12" r="2.5"/>
                          <circle cx="16" cy="12" r="2.5"/>
                          <path d="M12 6C8 6 4.5 8 3 11h3c.5-1.5 2-3 6-3s5.5 1.5 6 3h3c-1.5-3-5-5-9-5z"/>
                        </svg>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 text-sm">Tripadvisor</p>
                        <p className="text-xs text-gray-500">{t.leaveReview}</p>
                      </div>
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Main Floating Button */}
          <button
            onClick={() => setShowReviewMenu(!showReviewMenu)}
            className={`w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 active:scale-95 ${
              showReviewMenu ? 'rotate-45' : ''
            }`}
            style={{ 
              backgroundColor: themeConfig.primary_color || primaryColor,
              boxShadow: `0 4px 20px ${themeConfig.primary_color || primaryColor}50`
            }}
            data-testid="review-fab"
          >
            {showReviewMenu ? (
              <X className="h-6 w-6 text-white" />
            ) : (
              <Star className="h-6 w-6 text-white fill-white" />
            )}
          </button>
        </div>
      )}

      {/* Click outside to close review menu */}
      {showReviewMenu && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowReviewMenu(false)}
        />
      )}

      {/* Custom styles including theme fonts */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=Lora:wght@400;500;600&family=Merriweather:wght@400;700&family=Open+Sans:wght@400;500;600&display=swap');
        
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scale-in {
          from { 
            opacity: 0; 
            transform: scale(0.9) translateY(20px); 
          }
          to { 
            opacity: 1; 
            transform: scale(1) translateY(0); 
          }
        }
        @keyframes slide-up {
          from { 
            opacity: 0; 
            transform: translateY(100%); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
        .animate-scale-in {
          animation: scale-in 0.4s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .animate-slide-up {
          animation: slide-up 0.4s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .scroll-mt-40 {
          scroll-margin-top: 10rem;
        }
      `}</style>

      {/* Payment return modal (Stripe) */}
      {paymentResult && (
        <div className="fixed inset-0 z-[10000] bg-black/50 flex items-end sm:items-center justify-center" data-testid="payment-result-modal">
          <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl p-6 text-center space-y-4">
            {paymentResult.state === 'checking' && (
              <>
                <Loader2 className="h-12 w-12 mx-auto animate-spin text-gray-400" />
                <h2 className="font-heading text-lg font-bold text-gray-900">Vérification du paiement...</h2>
                <p className="text-sm text-gray-500">Un instant, nous confirmons votre paiement.</p>
              </>
            )}
            {paymentResult.state === 'paid' && (
              <>
                <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-500" />
                <h2 className="font-heading text-xl font-bold text-gray-900">Paiement confirmé !</h2>
                <p className="text-gray-500">
                  Votre commande {paymentResult.orderNumber && <strong>{paymentResult.orderNumber}</strong>} a été transmise au restaurant.
                </p>
                {paymentResult.trackingToken && (
                  <button
                    onClick={() => { window.location.href = `/order/${paymentResult.trackingToken}`; }}
                    className="w-full py-3 rounded-full text-white font-semibold text-sm"
                    style={{ backgroundColor: primaryColor }}
                    data-testid="payment-track-order-btn"
                  >
                    Suivre ma commande
                  </button>
                )}
                <button onClick={closePaymentModal} className="w-full py-3 rounded-full border border-gray-200 text-gray-600 font-medium text-sm" data-testid="payment-modal-close">
                  Fermer
                </button>
              </>
            )}
            {paymentResult.state === 'cancelled' && (
              <>
                <AlertCircle className="h-14 w-14 mx-auto text-amber-500" />
                <h2 className="font-heading text-xl font-bold text-gray-900">Paiement annulé</h2>
                <p className="text-gray-500 text-sm">Votre paiement n'a pas été effectué. Vous pouvez réessayer depuis votre panier.</p>
                <button onClick={closePaymentModal} className="w-full py-3 rounded-full border border-gray-200 text-gray-600 font-medium text-sm" data-testid="payment-modal-close">
                  Fermer
                </button>
              </>
            )}
            {paymentResult.state === 'timeout' && (
              <>
                <AlertCircle className="h-14 w-14 mx-auto text-amber-500" />
                <h2 className="font-heading text-xl font-bold text-gray-900">Vérification en attente</h2>
                <p className="text-gray-500 text-sm">Nous n'avons pas pu confirmer le paiement immédiatement. Si vous avez été débité, la commande a bien été transmise — rapprochez-vous du restaurant.</p>
                <button onClick={closePaymentModal} className="w-full py-3 rounded-full border border-gray-200 text-gray-600 font-medium text-sm" data-testid="payment-modal-close">
                  Fermer
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Cart component for ordering mode */}
      <MenuCart
        tableId={tableId}
        tableLabel={tableLabel}
        restaurantName={menuData?.restaurant?.name}
        orderingEnabled={orderingEnabled}
        primaryColor={primaryColor}
        paymentMode={menuData?.restaurant?.payment_mode || 'on_site'}
        stripeConfigured={menuData?.restaurant?.stripe_configured || false}
        orderType={orderType}
        onOrderTypeChange={setOrderType}
      />
    </div>
  );
};

export default PublicMenu;
